import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isEqual, isFunction, map } from 'lodash';
import classNames from 'classnames';
import { fieldProps, getPreferredProp } from '../utils';

function RadioField({
  options,
  className,
  labelClassName,
  id,
  style,
  inline,
  label,
  inlineLabel,
  postLabelContent,
  required,
  ...props
}) {
  const onChange = (value, disabled) => {
    if (!disabled) {
      const callback = getPreferredProp(props, 'onChange');
      if (isFunction(callback)) {
        callback(value);
      }

      if (isFunction(props.onChange) && !isEqual(props.onChange, getPreferredProp(props, 'onChange'))) {
        props.onChange(value);
      }
    }
  };

  const hasError = getPreferredProp(props, 'invalid') && getPreferredProp(props, 'touched');
  const error = getPreferredProp(props, 'error') || (required ? 'Required' : '');

  const containerClass = useMemo(() => classNames({
    'border-none my-2 mx-0 p-0 relative mb-2 w-full': true,
    'inline-block mr-2': inline,
    'text-red': hasError,
  }, className, style.container), [className, hasError, inline]);

  const labelClass = useMemo(() => classNames('text-dark-grey text-xs mb-0 normal-case tracking-normal font-semibold leading-4', {
    'text-red': hasError,
    'text-action-blue': !hasError,
    'inline-block pr-6 pl-0': inlineLabel,
  }, labelClassName, style.label), [inlineLabel, required, hasError, labelClassName]);

  const optionLabelClass = disabled => classNames(
    'flex gap-2 items-center tracking-normal leading-loose normal-case text-text-blue font-regular-font font-normal text-sm', {
      'cursor-pointer': !disabled,
      'opacity-50': disabled,
    });

  const inputClass = (checked, disabled) => classNames('appearance-none w-4 h-4 border-2 border-solid rounded-full shrink-0', {
    'border-medium-grey hover:bg-medium-fill-grey': !checked && !hasError && !disabled,
    'border-action-blue': checked && !hasError,
    'group-hover:border-deep-blue': checked && !hasError && !disabled,
    'border-red': hasError,
    'opacity-50': disabled,
    'focus:outline-solid-offset': !disabled,
  }, style.input);

  const selectedClass = (checked, disabled) => classNames('absolute w-2 h-2 rounded-full bg-action-blue ml-1', {
    'group-hover:bg-deep-blue': checked && !disabled,
    'opacity-50': disabled,
  });

  return (
    <fieldset
      className={containerClass}
      aria-labelledby={`uu-label-${id}`}
      id={`${id}-uu-radio-field__container`}
      data-testid={`${id}-uu-radio-field__container`}
    >
      {
        !isEmpty(label) &&
          <legend
            id={`uu-label-${id}`}
            data-testid={`uu-label-${id}`}
            className={labelClass}
          >
            {label}
            {required && <span className="ml-1 text-red">*</span>}
          </legend>
      }
      {postLabelContent}
      {
        map(options, (opt, index) => {
          const checked = getPreferredProp(props, 'value') === opt.value;
          return (
            <ul className={classNames({ 'inline-block mr-3': inline })} key={index}>
              <li className="w-full">
                <div className="flex items-center gap-2 group">
                  <input
                    className={inputClass(checked, opt.disabled)}
                    id={`${id}-uu-radio-field-${index}`}
                    data-testid={`${id}-uu-radio-field-${index}`}
                    type="radio"
                    name={getPreferredProp(props, 'name')}
                    checked={checked}
                    onChange={e => onChange(e, opt.disabled)}
                    onBlur={getPreferredProp(props, 'onBlur')}
                    onFocus={getPreferredProp(props, 'onFocus')}
                    disabled={opt.disabled}
                    value={opt.value}
                  />
                  {checked && <div className={selectedClass(checked, opt.disabled)} />}
                  <label
                    className={optionLabelClass(opt.disabled)}
                    htmlFor={`${id}-uu-radio-field-${index}`}
                    id={`${id}-uu-radio-field-${index}__label`}
                    data-testid={`${id}-uu-radio-field-${index}__label`}
                  >
                    {opt.label}
                  </label>
                </div>
              </li>
            </ul>
          );
        })
      }
      {error && hasError && (
        <div
          className={classNames('text-red text-xs', style.error)}
          id={`uu-${id}-radio-field__error`}
          data-testid={`${id}-uu-radio-field__error`}
        >
          {error}
        </div>
      )}
    </fieldset>
  );
}

RadioField.propTypes = {
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** className for reference */
  className: PropTypes.string,
  /** array of options consist of value and label */
  options: PropTypes.array,
  /** Text of the label */
  label: PropTypes.string,
  /** If true, Radio Buttons will be displayed inline */
  inline: PropTypes.bool,
  /** If true, Radio Label will be displayed inline */
  inlineLabel: PropTypes.bool,
  /** Content block that is placed between the label and the field */
  postLabelContent: PropTypes.node,
  /** Is the field required */
  required: PropTypes.bool,
  /** Override the inline-styles of the elements || **Not Recommended** */
  style: PropTypes.shape({
    container: PropTypes.string,
    label: PropTypes.string,
    text: PropTypes.string,
    error: PropTypes.string,
  }),
  /** See fieldProps doc */
  ...fieldProps,
};

RadioField.defaultProps = {
  id: '',
  className: '',
  options: [],
  inlineLabel: false,
  label: '',
  labelClassName: '',
  postLabelContent: null,
  required: false,
  style: {
    container: '',
    label: '',
    input: '',
    error: '',
  },
  inline: false,
};

export default RadioField;
